import { nanoid } from '@reduxjs/toolkit';
import { Dictionary, deepEqual, isObject, keys, objectMap, values } from '@direct-frontend/stdlib';

import { waitForSelector } from '@direct-frontend/modules-store';
import { produce } from 'immer';
import { getFieldInstance } from '../fields/getFieldInstance';
import { BaseFields, InferFieldsValue, $canSetWhileSubmitting } from '../fields/types';
import { getValidationState, validateAll, validateField } from '../fields/validation';
import { getFormStoreSingleton, getRegisteredFormStore } from '../store/store';
import { BaseValidationError } from '../types';

import { $internals as $formApiInternals, SubmitFormApi, createSubmitFormApi } from './formApi';
import { assertFormInitialized } from './utils/assertFormInitialized';
import { createFormStateGetters } from './utils/createFormStateGetters';
import { validateForm, waitForAllRunningValidations } from './validation';
import { startAllFormReactions, stopAllFormReactions, waitForAllRunningReactions } from './reactions';
import { walkFields } from '../fields/utils/walkFields';
import { FormAlreadyRegisteredError, registeredForms } from './registeredForms';
import { disposeForm } from './disposeForm';
import { getHotModule } from './hotModule';
import { scrollToFirstError } from './scrollToFirstError';
import { assertFormNotSubmitting } from './utils/assertFormNotSubmitting';
import { FormState } from '../store/types';
import {
    frozenInstancesContext,
    getOrCreateFrozenInstance as getOrCreateFrozenInstanceRaw,
} from './frozenInstancesContext';
import {
    $actions,
    $fields,
    $internals,
    BoundFormActions,
    BoundFormAsyncActions,
    FieldsInstances,
    FormActionsMap,
    FormAsyncActionsMap,
    FormDeclaration,
    FormOptions,
    FormReaction,
    SkipUnknownFormAsyncActions,
} from './types';
import { createReusableReactionApi } from '../fields/instanceHelpers';
import { abortAllRunningAsyncActions, waitForAllRunningAsyncActions } from './actions';
import { bindFormAsyncActions } from './bindFormAsyncActions';
import { generateNewFormInitializationId } from '../store/utils/formInitializationId';
import {
    FORM_SUBMIT_MEASURE_NAME,
    FORM_KIT_PERFORMANCE_GROUP,
    FORM_SUBMIT_START_MARK_NAME,
    FORM_SUBMIT_FINISH_MARK_NAME,
} from '../utils/performanceHelpers';

/**
 * Стандартная функция для обработки ошибок при отправке формы
 *
 * По дефолту скроллит к первой ошибке
 */
function defaultOnSubmitFailed<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError = never,
    FormContextInput = never,
    FormContext = never,
>(formApi: SubmitFormApi<FormFields, FormValidationError, FormContext, FormContextInput>) {
    formApi.scrollToFirstError();
}

const PERFORMANCE_DEVTOOLS_SUBMIT = {
    trackGroup: FORM_KIT_PERFORMANCE_GROUP,
    track: FORM_SUBMIT_MEASURE_NAME,
};

/**
 * Создает форму
 * @returns Форма, которую можно использовать в React-компонентах или императивном коде
 */
export function createForm<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput = never,
    FormContext = never,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext> = FormActionsMap<
        FormFields,
        FormValidationError,
        FormContext
    >,
    FormSubmitArg = never,
    FormSubmitReturnValue = unknown,
    FormAsyncActions extends FormAsyncActionsMap<
        FormFields,
        FormValidationError,
        FormContext,
        FormActions
    > = FormAsyncActionsMap<FormFields, FormValidationError, FormContext, FormActions>,
>({
    name,
    fields: validatedFields,
    validators,
    reactions,
    actions,
    asyncActions: asyncActionsOption,
    onSubmit,
    onSubmitFailed = defaultOnSubmitFailed,
    context,
    ...restOptions
}: FormOptions<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue,
    FormAsyncActions
>): FormDeclaration<
    FormName,
    FormFields,
    FormValidationError,
    FormContextInput,
    FormContext,
    FormActions,
    FormSubmitArg,
    FormSubmitReturnValue,
    SkipUnknownFormAsyncActions<FormAsyncActions>
> {
    if (registeredForms.has(name)) {
        throw new FormAlreadyRegisteredError('Form already registered', {
            additional: { name },
            cause: null,
        });
    }
    registeredForms.add(name);

    // При горячей замене модуля с формой снимаем её с регистрации, иначе повторный createForm бросит ошибку выше.
    // HMR-объект модуля формы подставляет swc-плагин dev-сборки. Без HMR условие статически ложно,
    // и сборщик вырезает ветку вместе с disposeForm и getHotModule из production-бандла
    if (module.hot) {
        getHotModule(restOptions)?.dispose(() => disposeForm(name));
    }

    // Приводим тип к корректному, так как в параметрах функции на уровне типа сделана валидация
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    const fields = validatedFields as FormFields;

    const formDeclaration: FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue,
        FormAsyncActions
    > = {
        name,
        getInstance: (formId: string) => {
            const formStore = getFormStoreSingleton(name);
            const {
                addFormValidationErrors,
                addSubmitErrors,
                reinitializeForm,
                cleanupForm,
                getAreReactionsStarted,
                getFormContext,
                getFormState,
                getFormValue,
                getSubscriptions,
                getFormInitializationIdsBySubscriptionId,
                initializeForm,
                isFormInitialized,
                resetForm,
                useIsFormInitialized,
                useIsFormSubmitting,
                subscribe,
                unsubscribe,
                updateFormAndFieldsContext,
                updateFormState,
                useFormContext,
                useFormState,
                useFormValue,
                getFieldsState,
                getFieldsContext,
                getFormInitializationId,
                useFormInitializationId,
                isFormHydratedFromServer,
                getIsUninitializing,
                setIsUninitializing,
            } = formStore;

            let frozenFormInitializationId: string | null = null;
            let boundAsyncActions: BoundFormAsyncActions<FormAsyncActions> | undefined;
            let internals: ReturnType<typeof formDeclaration.getInstance>[typeof $internals] | undefined;

            // Функция для получения актуального или зафиксированного ID
            const getCurrentFormInitializationId = () => {
                return frozenFormInitializationId || getFormInitializationId({ formId }) || formId;
            };

            const useCurrentFormInitializationId = () => {
                const formInitializationId = useFormInitializationId({ formId });

                return frozenFormInitializationId || formInitializationId || formId;
            };

            /**
             * Создает новый инстанс формы с замороженным ID и сохраняет его в frozenInstances
             */
            const getOrCreateFrozenInstance = (initializationId: string) => {
                return getOrCreateFrozenInstanceRaw(initializationId, formDeclaration.getInstance(formId));
            };

            return {
                name,
                id: formId,

                get actions() {
                    // Инициализируем кэш
                    if (!this[$actions]) {
                        const {
                            getFormValue,
                            setFormValue,
                            getFieldsState,
                            getFormState,
                            getFormContext,
                            touch,
                            setParentsStateByKey,
                        } = getFormStoreSingleton(name);

                        // Если нет actions, то возвращаем пустой объект
                        if (!actions) {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions --- кейс с отсутствующим actions
                            this[$actions] = {} as BoundFormActions<FormActions>;
                            return this[$actions];
                        }

                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions --- objectMap не выведет правильно тип, выводим сами
                        this[$actions] = objectMap(actions, action => (...args: unknown[]) => {
                            const formInitializationId = getCurrentFormInitializationId();
                            const formValue = getFormValue({ formInitializationId });

                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Тип гарантирован публичным интерфейсом
                            const nextValue = produce(formValue, draft => {
                                const actionParams = {
                                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Тип гарантирован публичным интерфейсом
                                    draftValue: draft as InferFieldsValue<FormFields>,
                                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Тип гарантирован публичным интерфейсом
                                    getContext: () => getFormContext({ formInitializationId }) as FormContext,
                                    getState: () =>
                                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Тип гарантирован публичным интерфейсом
                                        getFormState({ formInitializationId }) as FormState<
                                            InferFieldsValue<FormFields>,
                                            FormValidationError
                                        >,
                                };

                                return action(actionParams, ...args);
                            }) as InferFieldsValue<FormFields>;

                            setFormValue({
                                formInitializationId,
                                formFields: this[$internals].fields,
                                formValue: nextValue,
                            });

                            const fieldsState = getFieldsState({ formInitializationId }) ?? {};

                            walkFields({
                                fields: this[$internals].fields,
                                fieldsState,
                                formValue: nextValue,
                                callback: (field, fieldId) => {
                                    validateField({
                                        cause: 'revalidate',
                                        form: this,
                                        field,
                                        fieldId,
                                        notRevalidateInnerFields: true,
                                    });

                                    touch({ formInitializationId, fieldId });
                                    setParentsStateByKey({
                                        fieldId,
                                        formInitializationId,
                                        formFields: this[$internals].fields,
                                    });
                                },
                            });
                        }) as BoundFormActions<FormActions>;
                    }

                    return this[$actions];
                },

                get asyncActions() {
                    if (!boundAsyncActions) {
                        boundAsyncActions = bindFormAsyncActions(
                            this,
                            asyncActionsOption?.(x => x),
                            frozenForm => ({
                                // Поля с setterOptions $canSetWhileSubmitting — чтобы экшен мог менять значения
                                // в фазе ожидания сабмита, когда submit ждёт завершения экшенов
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- objectMap не выведет правильно тип
                                fields: objectMap(fields, (field, fieldName) =>
                                    getFieldInstance({
                                        form: frozenForm,
                                        field,
                                        fieldName,
                                        fieldId: fieldName,
                                        creationOptions: { setterOptions: { [$canSetWhileSubmitting]: true } },
                                    })
                                ) as FieldsInstances<FormFields>,
                                getValue: frozenForm.getValue,
                                getContext: frozenForm.getContext,
                                getState: frozenForm.getState,
                                state: frozenForm.state,
                                actions: frozenForm.actions,
                                addValidationError: frozenForm.addValidationError,
                                addValidationErrors: frozenForm.addValidationErrors,
                            })
                        );
                    }
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- boundAsyncActions всегда устанавливается в блоке выше
                    return boundAsyncActions!;
                },

                getValue: () => {
                    const formInitializationId = getCurrentFormInitializationId();

                    assertFormInitialized({ formName: name, formId, formInitializationId });
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                    return getFormValue({ formInitializationId }) as InferFieldsValue<FormFields>;
                },
                getState: () => {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                    return getFormState({ formInitializationId }) as FormState<
                        InferFieldsValue<FormFields>,
                        FormValidationError
                    >;
                },
                state: createFormStateGetters<InferFieldsValue<FormFields>, FormValidationError>(
                    { name, id: formId },
                    getCurrentFormInitializationId
                ),
                getContext: () => {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                    return getFormContext({ formInitializationId }) as FormContext;
                },

                setContext(input: FormContextInput) {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });

                    const { form: formContext, fields: fieldsContextArgs }: Dictionary<unknown> =
                        context?.(input) ?? {};

                    const formValue = getFormValue({ formInitializationId }) ?? {};
                    const fieldsState = getFieldsState({ formInitializationId }) ?? {};

                    // Запоминаем контекст формы и всех полей ДО обновления
                    const formContextBefore = getFormContext({ formInitializationId });
                    const fieldsContextBefore = getFieldsContext({ formInitializationId }) ?? {};

                    // Обновляем контекст формы и всех полей
                    updateFormAndFieldsContext({
                        formInitializationId,
                        fields,
                        fieldsContextArgs: isObject(fieldsContextArgs) ? fieldsContextArgs : {},
                        formContext,
                    });

                    // Если значение контекста формы изменилось – перезапускаем её валидацию
                    if (!deepEqual(getFormContext({ formInitializationId }), formContextBefore)) {
                        validateForm('revalidate', getValidationState(this), this);
                    }

                    // Если значение контекста какого-то поля изменилось – перезапускаем его валидацию
                    walkFields({
                        fields,
                        fieldsState,
                        formValue,
                        callback: (field, fieldId) => {
                            const instance = getFieldInstance({ form: this, field, fieldName: fieldId, fieldId });

                            if (!deepEqual(instance.getContext(), fieldsContextBefore[fieldId])) {
                                validateField({
                                    cause: 'revalidate',
                                    form: this,
                                    field,
                                    fieldId,
                                    notRevalidateInnerFields: true,
                                });
                            }
                        },
                    });
                },

                // Используем геттер, чтобы обеспечить доступ к полям, но вместе с тем иметь доступ к `this`
                get fields() {
                    // Кешируем объект с полями, чтобы не вычислять его каждый раз
                    if (!this[$fields]) {
                        // `objectMap` перемешивает значения между ключами, поэтому приводим тип к более точному
                        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                        this[$fields] = objectMap(fields, (field, fieldName) =>
                            getFieldInstance({ form: this, field, fieldName, fieldId: fieldName })
                        ) as FieldsInstances<FormFields>;
                    }

                    return this[$fields];
                },

                revalidate() {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });

                    validateAll({ cause: 'revalidate', form: this });
                },

                async submit(...submitArgs) {
                    const startPerformanceMark = performance.mark?.(FORM_SUBMIT_START_MARK_NAME, {
                        detail: {
                            formId,
                            formName: name,
                            devtools: PERFORMANCE_DEVTOOLS_SUBMIT,
                        },
                    });

                    const prepareFormApi = () => {
                        const instance = formDeclaration.getInstance(formId);

                        const validationState = getValidationState(instance);
                        // Создаем fieldApi, чтобы передать его в onSubmit или onSubmitFailed
                        const formApi = createSubmitFormApi(
                            'onSubmit',
                            { cause: 'submit', form: instance },
                            validationState
                        );
                        // Переключаем его в режим записи в слайс, так как функции могут быть асинхронными
                        formApi[$formApiInternals].updateOptions({ storageType: 'slice' });

                        return formApi;
                    };

                    // Хелпер для получения флага наличия ошибок валидаций формы и полей, кроме onSubmit формы
                    // В случае onSubmit ошибок формы они не будут сбрасываться при триггере сабмита до следующего запуска submit валидаторов
                    const getHasErrorsBeforeSubmit = () => {
                        const hasFieldsErrors = values(getFieldsState({ formInitializationId }) ?? {}).some(
                            state =>
                                state.errorsMap.onSubmit.length > 0 ||
                                state.errorsMap.onChange.length > 0 ||
                                state.errorsMap.onBlur.length > 0
                        );
                        return (
                            hasFieldsErrors ||
                            (getFormState({ formInitializationId })?.errorsMap.onBlur.length ?? 0 > 0) ||
                            (getFormState({ formInitializationId })?.errorsMap.onChange.length ?? 0 > 0)
                        );
                    };

                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });

                    // Пока сабмит ждёт, форму могут уничтожить: в тестах это делает cleanupForms
                    const isFormDestroyed = () =>
                        getRegisteredFormStore(name) !== formStore || !isFormInitialized({ formInitializationId });

                    updateFormState({
                        formInitializationId,
                        state: { isSubmitting: true, submitErrors: [], isAwaitingStartedAsyncSetters: true },
                    });

                    // Дожидаемся завершения выполнения асинхронных экшенов формы и всех её полей
                    await waitForAllRunningAsyncActions(this);

                    if (isFormDestroyed()) {
                        return;
                    }

                    // Дожидаемся завершения выполнения запущенных реакций формы и всех её полей
                    await waitForAllRunningReactions(this);

                    if (isFormDestroyed()) {
                        return;
                    }

                    updateFormState({ formInitializationId, state: { isAwaitingStartedAsyncSetters: false } });

                    // Если форму отправить нельзя или в ней есть ошибки валидации, то сразу выходим
                    if (getHasErrorsBeforeSubmit()) {
                        try {
                            await onSubmitFailed?.(prepareFormApi());
                        } catch (error) {
                            console.error(error);
                        } finally {
                            updateFormState({ formInitializationId, state: { isSubmitting: false } });
                        }
                        return;
                    }

                    // Дожидаемся завершения выполнения асинхронных change и blur валидаторов формы и всех её полей
                    await waitForAllRunningValidations(this, ['onChange', 'onBlur']);

                    if (isFormDestroyed()) {
                        return;
                    }

                    // Если после валидации в форме есть ошибки, то выходим
                    if (getHasErrorsBeforeSubmit()) {
                        try {
                            await onSubmitFailed?.(prepareFormApi());
                        } catch (error) {
                            console.error(error);
                        } finally {
                            updateFormState({ formInitializationId, state: { isSubmitting: false } });
                        }
                        return;
                    }

                    // Запускаем submit валидаторы формы и всех её полей
                    validateAll({ cause: 'submit', form: this });
                    // Дожидаемся завершения выполнения асинхронных валидаторов формы и всех её полей
                    await waitForAllRunningValidations(this, ['onSubmit']);

                    if (isFormDestroyed()) {
                        return;
                    }

                    // Если после валидации в форме есть ошибки, то выходим
                    if (!getFormState({ formInitializationId })?.isValid) {
                        const formApi = prepareFormApi();
                        try {
                            await onSubmitFailed?.(formApi);
                        } catch (error) {
                            console.error(error);
                        } finally {
                            updateFormState({ formInitializationId, state: { isSubmitting: false } });
                        }
                        return;
                    }

                    let isSubmitSuccessful = false;

                    try {
                        const submitResult = await onSubmit(
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                            getFormValue({ formInitializationId }) as InferFieldsValue<FormFields>,
                            prepareFormApi(),
                            ...submitArgs
                        );

                        isSubmitSuccessful = true;

                        return submitResult;
                    } finally {
                        if (!isFormDestroyed()) {
                            const formState = getFormState({ formInitializationId });

                            if (!formState?.isValid || formState?.submitErrors.length > 0) {
                                const formApi = prepareFormApi();
                                try {
                                    await onSubmitFailed?.(formApi);
                                } catch (error) {
                                    console.error(error);
                                }
                            }
                            updateFormState({ formInitializationId, state: { isSubmitting: false } });
                        }

                        const endPerformanceMark = performance.mark?.(FORM_SUBMIT_FINISH_MARK_NAME, {
                            detail: {
                                formId,
                                formName: name,
                                isSubmitSuccessful,
                                devtools: PERFORMANCE_DEVTOOLS_SUBMIT,
                            },
                        });
                        performance.measure?.(FORM_SUBMIT_MEASURE_NAME, {
                            start: startPerformanceMark.startTime,
                            end: endPerformanceMark.startTime,
                            detail: {
                                formId,
                                formName: name,
                                isSubmitSuccessful,
                                devtools: {
                                    ...PERFORMANCE_DEVTOOLS_SUBMIT,
                                    tooltipText: `Form ${name}:${formId} submitting`,
                                },
                            },
                        });
                    }
                },

                addValidationError(error) {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });

                    addFormValidationErrors({
                        formInitializationId,
                        validatorType: 'onSubmit',
                        validationErrors: [
                            {
                                ...error,
                                meta: {
                                    source: {
                                        type: 'form',
                                    },
                                },
                            },
                        ],
                    });
                },
                addValidationErrors(errors) {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });

                    addFormValidationErrors({
                        formInitializationId,
                        validatorType: 'onSubmit',
                        validationErrors: errors.map(error => ({
                            ...error,
                            meta: {
                                source: {
                                    type: 'form',
                                },
                            },
                        })),
                    });
                },

                addSubmitError(error) {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });

                    addSubmitErrors({
                        formInitializationId,
                        errors: [error],
                    });
                },

                reset() {
                    const formInitializationId = getCurrentFormInitializationId();
                    // Если формы нет - то и сбрасывать уже нечего
                    assertFormInitialized({ formName: name, formId, formInitializationId: formInitializationId });

                    if (getFormState({ formInitializationId: formInitializationId })?.isSubmitting) {
                        return;
                    }

                    const frozenInstances = frozenInstancesContext.getContext();
                    const frozenInstance = frozenInstances[formInitializationId];
                    if (frozenInstance) {
                        abortAllRunningAsyncActions(frozenInstance);

                        Promise.allSettled(stopAllFormReactions({ form: frozenInstance })).then(() => {
                            delete frozenInstances[formInitializationId];
                        });
                        // Выставляем после запуска остановки реакций
                        setIsUninitializing({ formInitializationId: formInitializationId, isUninitializing: true });
                    }

                    // resetForm создаст новый formInitializationId внутри
                    resetForm({ fields, formId, formInitializationId });

                    // Создаем новый инстанс с замороженным ID
                    const newFormInitializationId = getFormInitializationId({ formId }) || formId;
                    const newFrozenInstance = getOrCreateFrozenInstance(newFormInitializationId);
                    startAllFormReactions({ form: newFrozenInstance });
                },

                reinitialize(formValue, contextArg) {
                    const formInitializationId = getCurrentFormInitializationId();
                    // В инстансе формы проверяем что форма инициализирована
                    // И не находится в процессе отправки
                    assertFormInitialized({ formName: name, formId, formInitializationId });
                    assertFormNotSubmitting('Cannot reinitialize form while it is submitting', {
                        formName: name,
                        formId,
                        formInitializationId,
                    });

                    return this[$internals].reinitialize(formValue, contextArg);
                },

                subscribe() {
                    const initialFormInitializationIdForSubscription = getCurrentFormInitializationId();

                    assertFormInitialized({
                        formName: name,
                        formId,
                        formInitializationId: initialFormInitializationIdForSubscription,
                    });
                    const subscriptionId = nanoid();
                    subscribe({ formInitializationId: initialFormInitializationIdForSubscription, subscriptionId });
                    return () => {
                        const formInitializationIdsForSubscription = getFormInitializationIdsBySubscriptionId({
                            subscriptionId,
                        });

                        return Promise.allSettled(
                            formInitializationIdsForSubscription.map(formInitializationIdForSubscription => {
                                const subscriptions = getSubscriptions({
                                    formInitializationId: formInitializationIdForSubscription,
                                });

                                // Если будет удалена последняя подписка, то нужно остановить запущенные реакции
                                // Останавливаем перед вызовом unsubscribe, так как реакции хранятся в сторе
                                if (
                                    subscriptions &&
                                    keys(subscriptions).length === 1 &&
                                    Boolean(subscriptions[subscriptionId])
                                ) {
                                    const frozenInstances = frozenInstancesContext.getContext();
                                    const frozenInstance = frozenInstances[formInitializationIdForSubscription];

                                    let runningReactionsPromises: Promise<void>[] = [];

                                    if (frozenInstance) {
                                        runningReactionsPromises = stopAllFormReactions({
                                            form: frozenInstance,
                                        });

                                        // Выставляем после запуска остановки реакций
                                        setIsUninitializing({
                                            formInitializationId: formInitializationIdForSubscription,
                                            isUninitializing: true,
                                        });
                                    }

                                    // Перед тем как отписаться
                                    return Promise.allSettled([
                                        // ждем завершения всех экшенов,
                                        frozenInstance
                                            ? waitForAllRunningAsyncActions(frozenInstance)
                                            : Promise.resolve(),
                                        // ждем завершения всех валидаций,
                                        frozenInstance
                                            ? waitForAllRunningValidations(frozenInstance, [
                                                  'onBlur',
                                                  'onChange',
                                                  'onSubmit',
                                              ])
                                            : Promise.resolve(),
                                        // завершения сабмита формы
                                        waitForSelector(
                                            () =>
                                                getFormState({
                                                    formInitializationId: formInitializationIdForSubscription,
                                                })?.isSubmitting === false
                                        ),
                                        // и завершения всех реакций
                                        ...runningReactionsPromises,
                                    ]).finally(() => {
                                        unsubscribe({
                                            formInitializationId: formInitializationIdForSubscription,
                                            formId,
                                            subscriptionId,
                                        });

                                        const remainingSubscriptions = getSubscriptions({
                                            formInitializationId: formInitializationIdForSubscription,
                                        });

                                        if (!remainingSubscriptions?.length) {
                                            delete frozenInstances[formInitializationIdForSubscription];
                                        }
                                    });
                                }

                                return unsubscribe({
                                    formInitializationId: formInitializationIdForSubscription,
                                    formId,
                                    subscriptionId,
                                });
                            })
                        );
                    };
                },

                isInitialized: () => {
                    try {
                        const formInitializationId = getCurrentFormInitializationId();
                        return isFormInitialized({ formInitializationId });
                    } catch {
                        return false;
                    }
                },

                scrollToFirstError() {
                    const formInitializationId = getCurrentFormInitializationId();
                    assertFormInitialized({ formName: name, formId, formInitializationId });
                    scrollToFirstError({ form: this });
                },

                // Скрываем из публичного API методы, которые используются только во внутренней реализации
                // Объект и фабрики кешируются отдельно для каждого инстанса формы.
                get [$internals]() {
                    if (internals) {
                        return internals;
                    }

                    let resolvedValidators: ReturnType<NonNullable<typeof validators>> | undefined;
                    let validatorsResolved = false;
                    let resolvedReactions: ReturnType<NonNullable<typeof reactions>> | undefined;
                    let reactionsResolved = false;

                    /**
                     * Записывает начальное состояние формы в стор. Без `shouldReinitialize`
                     * идемпотентно: если форма уже проинициализирована, ничего не делает.
                     * С `shouldReinitialize: true` идемпотентности нет – каждый вызов заводит
                     * новый `formInitializationId`, поэтому в рендер такой вызов пускать нельзя.
                     * Вызывается синхронно в рендере на сервере всегда, на клиенте – только
                     * на проходе гидрации и только когда разрешил `canInitializeDuringRender`.
                     */
                    const initializeState = (
                        formValue?: InferFieldsValue<FormFields>,
                        contextArg?: FormContextInput,
                        options?: { shouldReinitialize?: boolean }
                    ) => {
                        const formInitializationId = options?.shouldReinitialize
                            ? generateNewFormInitializationId(formId)
                            : getCurrentFormInitializationId();

                        // Инициализируем только один раз
                        // Форма считается проинициализированной если в сторе есть данные
                        if (
                            !isFormInitialized({ formInitializationId }) ||
                            getIsUninitializing({ formInitializationId })
                        ) {
                            // Инициализируем с НОВЫМ композитным ключом
                            const contextResult: Dictionary<unknown> =
                                // Наличие, в случае необходимости, параметра гарантируется типами публичного интерфейса
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                                context?.(contextArg as FormContextInput) ?? {};

                            initializeForm({
                                formId,
                                fields,
                                fieldsContextArgs: isObject(contextResult.fields) ? contextResult.fields : {},
                                // Если данные для инициализации не переданы, то инициализируем форму данными из полей
                                formValue: formValue ?? objectMap(fields, field => field.defaultValue),
                                formContext: contextResult.form,
                            });
                        }

                        getOrCreateFrozenInstance(getFormInitializationId({ formId }) || formId);
                    };

                    /**
                     * Запускает реакции формы. Реакции – пользовательский код с запросами, поэтому
                     * им место только в эффекте: React вправе отбросить и повторить рендер.
                     */
                    const startReactions = () => {
                        const formInitializationId = getFormInitializationId({ formId }) || formId;

                        // Проверка вынесена из условия инициализации, чтобы в кейсе гидратации был запуск реакций
                        if (
                            typeof window === 'undefined' ||
                            !isFormInitialized({ formInitializationId }) ||
                            getAreReactionsStarted({ formInitializationId })
                        ) {
                            return;
                        }

                        startAllFormReactions({ form: getOrCreateFrozenInstance(formInitializationId) });
                    };
                    type CurrentFormReaction = FormReaction<
                        FormName,
                        FormFields,
                        FormValidationError,
                        NoInfer<FormActions>,
                        FormContextInput,
                        FormContext,
                        FormSubmitArg,
                        FormSubmitReturnValue
                    >;

                    const createReactions = (reactionsList: CurrentFormReaction[]): CurrentFormReaction[] =>
                        reactionsList;
                    internals = {
                        fields,
                        get validators() {
                            if (!validatorsResolved) {
                                resolvedValidators = validators?.(value => value ?? {});
                                validatorsResolved = true;
                            }

                            return resolvedValidators;
                        },
                        get reactions() {
                            if (!reactionsResolved) {
                                resolvedReactions = reactions?.(createReactions, createReusableReactionApi());
                                reactionsResolved = true;
                            }

                            return resolvedReactions;
                        },

                        get formInitializationId() {
                            return getCurrentFormInitializationId();
                        },

                        get isFrozenInitializationId() {
                            return Boolean(frozenFormInitializationId);
                        },

                        // Было ли состояние формы поднято с сервера при гидрации.
                        // В этом случае при первом маунте форму не нужно переинициализировать —
                        // серверный снапшот должен быть консьюмлен, а не затёрт.
                        get isHydratedFromServer() {
                            return isFormHydratedFromServer({ formId });
                        },

                        useFormInitializationId: () => {
                            return useCurrentFormInitializationId();
                        },

                        freezeInitializationId: (initializationId: string) => {
                            frozenFormInitializationId = initializationId;
                        },

                        useIsFormInitialized: () => {
                            const formInitializationId = useCurrentFormInitializationId();
                            return useIsFormInitialized({ formInitializationId });
                        },

                        useValue: () => {
                            const formInitializationId = useCurrentFormInitializationId();
                            assertFormInitialized({ formName: name, formId, formInitializationId });
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                            return useFormValue({ formInitializationId }) as InferFieldsValue<FormFields>;
                        },
                        useState: () => {
                            const formInitializationId = useCurrentFormInitializationId();

                            assertFormInitialized({ formName: name, formId, formInitializationId });
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                            return useFormState({ formInitializationId }) as FormState<
                                InferFieldsValue<FormFields>,
                                FormValidationError
                            >;
                        },
                        useIsFormSubmitting: () => {
                            const formInitializationId = useCurrentFormInitializationId();
                            return useIsFormSubmitting({ formInitializationId });
                        },
                        useContext: () => {
                            const formInitializationId = useCurrentFormInitializationId();
                            assertFormInitialized({ formName: name, formId, formInitializationId });
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Выше есть ассерт
                            return useFormContext({ formInitializationId }) as FormContext;
                        },

                        getSubscriptions: () => {
                            const formInitializationId = getCurrentFormInitializationId();
                            assertFormInitialized({ formName: name, formId, formInitializationId });
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- Выше есть ассерт
                            return getSubscriptions({ formInitializationId })!;
                        },

                        cleanup: () => {
                            const formInitializationId = getCurrentFormInitializationId();
                            assertFormInitialized({ formName: name, formId, formInitializationId });

                            const frozenInstances = frozenInstancesContext.getContext();
                            const frozenInstance = frozenInstances[formInitializationId];

                            // Останавливаем перед вызовом cleanup так как реакции хранятся в сторе
                            if (frozenInstance) {
                                Promise.allSettled(stopAllFormReactions({ form: frozenInstance })).then(() => {
                                    delete frozenInstances[formInitializationId];
                                });
                                // Выставляем после запуска остановки реакций
                                setIsUninitializing({
                                    formInitializationId: formInitializationId,
                                    isUninitializing: true,
                                });
                            }

                            cleanupForm({ formInitializationId });
                        },
                        canInitializeDuringRender: () => {
                            // Первая инициализация formId идемпотентна – ее можно повторить,
                            // если React отбросит рендер. А вот запись поверх уже готовой формы
                            // (реинициализация, доинициализация после uninitialize) – нет,
                            // такой вызов остается в эффекте.
                            const formInitializationId = getCurrentFormInitializationId();

                            return (
                                !isFormInitialized({ formInitializationId }) &&
                                !getIsUninitializing({ formInitializationId })
                            );
                        },
                        initializeState,
                        startReactions,
                        initialize: (
                            formValue?: InferFieldsValue<FormFields>,
                            contextArg?: FormContextInput,
                            options?: { shouldReinitialize?: boolean }
                        ) => {
                            initializeState(formValue, contextArg, options);
                            startReactions();
                        },
                        reinitialize: (formValue?: InferFieldsValue<FormFields>, contextArg?: FormContextInput) => {
                            const formInitializationId = getCurrentFormInitializationId();

                            const frozenInstances = frozenInstancesContext.getContext();
                            const frozenInstance = frozenInstances[formInitializationId];

                            // Останавливаем реакции старой инициализации
                            if (frozenInstance) {
                                abortAllRunningAsyncActions(frozenInstance);
                                Promise.allSettled([stopAllFormReactions({ form: frozenInstance })]).then(() => {
                                    delete frozenInstances[formInitializationId];
                                });

                                // Выставляем после запуска остановки реакций
                                setIsUninitializing({
                                    formInitializationId: formInitializationId,
                                    isUninitializing: true,
                                });
                            }

                            const contextResult: Dictionary<unknown> | undefined =
                                // Наличие, в случае необходимости, параметра гарантируется типами публичного интерфейса
                                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                                contextArg ? context?.(contextArg as FormContextInput) : undefined;

                            // reinitializeForm создаст новый formInitializationId внутри
                            reinitializeForm({
                                formInitializationId,
                                formId,
                                fields,
                                fieldsContextArgs: isObject(contextResult?.fields) ? contextResult?.fields : undefined,
                                // Если данные для инициализации не переданы, то инициализируем форму данными из полей
                                formValue: formValue ?? objectMap(fields, field => field.defaultValue),
                                formContext: contextResult?.form,
                            });

                            const newFormInitializationId = getFormInitializationId({ formId }) || formId;
                            const newFrozenInstance = getOrCreateFrozenInstance(newFormInitializationId);

                            // Запускаем реакции только при вызове на клиенте
                            if (
                                typeof window !== 'undefined' &&
                                !getAreReactionsStarted({ formInitializationId: newFormInitializationId })
                            ) {
                                startAllFormReactions({ form: newFrozenInstance });
                            }

                            // Инстанс с новым initializationId, без фриза
                            return formDeclaration.getInstance(formId);
                        },
                    };

                    return internals;
                },
            };
        },
    };

    // В возвращаемом типе `FormAsyncActions` уточняется через `SkipUnknownFormAsyncActions`
    // (отсутствующие экшены схлопываются в `never`), поэтому приводим тип явно
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
    return formDeclaration as FormDeclaration<
        FormName,
        FormFields,
        FormValidationError,
        FormContextInput,
        FormContext,
        FormActions,
        FormSubmitArg,
        FormSubmitReturnValue,
        SkipUnknownFormAsyncActions<FormAsyncActions>
    >;
}
